import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  Column,
  useTable,
  useGlobalFilter,
  useFilters,
  TableInstance,
  HeaderGroup,
  ColumnInstance,
  UseSortByColumnProps,
  useSortBy,
} from 'react-table';
import { TaskData } from '../task-details-widget/TaskDataModels';
import { ReactComponent as PencilIcon } from './../../images/icon-pencil.svg';
import { ReactComponent as TrashIcon } from './../../images/trash.svg';
import './DataTable.scss';
import './TaskDataTable.scss';
import SuccessfullToast from '../../features/common/toast/SuccessfullToast';
import ErrorToast from '../../features/common/toast/ErrorToast';
import { useAppSelector } from '../../hooks/storeHooks';
import { DefaultColumnFilter } from './helpers/DefaultColumnFilter';
import { createMultiConditionFilter } from '../../utilities/tableFilters';
import { ProtectAction } from '../../rbac';
import Loader from '../../features/common/Loader/Loader';
import { formatDate } from '../../utilities/dateHelpers';

interface TaskDataTableProps {
  visibleColumns: string[];
  searchTerm: string;
  taskData: TaskData[];
  onEditTask: (task: TaskData) => void;
  onDeleteTask: (task: TaskData) => void;
  onUpdateVisibleColumns?: (columns: string[]) => void;
  onMarkAsCompleteTask: (task: TaskData) => void;
  applyTrigger?: boolean;
  isConfigCheckBoxEnabled?: boolean;
  isResetClicked?: boolean;
  onFilteredSortedDataReady?: (data: TaskData[]) => void;
  rPermission: { [key: string]: boolean };
  isRenderFromTaskTab?: boolean;
  isLoading?: boolean;
}

const TaskDataTable: React.FC<TaskDataTableProps> = ({
  visibleColumns,
  searchTerm,
  taskData,
  onEditTask,
  onDeleteTask,
  onMarkAsCompleteTask,
  applyTrigger,
  isResetClicked,
  onFilteredSortedDataReady,
  rPermission,
  isRenderFromTaskTab = false,
  isLoading = false,
}) => {
  const [saveConfigResponse] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentFilters, setCurrentFilters] = useState<any[]>([]);
  const data = useMemo(() => {
    const sorted = [...(taskData || [])].sort((a, b) => {
      const dateA = new Date(a.Created).getTime();
      const dateB = new Date(b.Created).getTime();
      return dateB - dateA;
    });
    return sorted;
  }, [taskData]);

  const pumpCheckerPageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const wellTestWidget = pumpCheckerPageData?.find((obj: any) => obj.key === 'taskDetails');
  const columnObjectsTask = wellTestWidget?.widgetProperty?.widgetProperties || [];

  const currentFiltersRef = useRef(currentFilters);

  useEffect(() => {
    currentFiltersRef.current = currentFilters; // Keep the ref updated with the latest value of currentFilters
  }, [currentFilters]);

  const multiConditionFilter = createMultiConditionFilter(setCurrentFilters, currentFiltersRef);

  const allColumns = useMemo<Column<TaskData>[]>(
    () => [
      {
        Header: '',
        id: 'actions',
        width: 80,
        Cell: ({ row }: { row: { original: TaskData } }) => (
          <div className='tdt-action-cell'>
            <ProtectAction canAccess={rPermission?.canEdit}>
              <PencilIcon onClick={() => onEditTask(row.original)} />
            </ProtectAction>
            <ProtectAction canAccess={rPermission?.canDelete}>
              <TrashIcon onClick={() => onDeleteTask(row.original)} />
            </ProtectAction>
          </div>
        ),
      },
      { Header: 'Task no.', accessor: 'TaskNumber', filter: multiConditionFilter },
      { Header: 'Category', accessor: 'CategoryName', filter: multiConditionFilter },
      {
        Header: 'Task',
        accessor: 'Task',
        Cell: ({ value }: any) => <div className='task-multiline-cell'>{value}</div>,
        filter: multiConditionFilter,
      },
      {
        Header: 'Task comment',
        accessor: 'Comment',
        Cell: ({ value }: any) => <div className='task-multiline-cell'>{value}</div>,
        filter: multiConditionFilter,
      },
      {
        Header: 'Created date',
        accessor: 'Created',
        Cell: ({ value }: { value: string }) => <span>{formatDate(value, true)}</span>,
      },
      {
        Header: 'Due date',
        accessor: 'DueDateString',
        Cell: ({ value }: { value: string }) => <span>{formatDate(value)}</span>,
        filter: multiConditionFilter,
      },
      { Header: 'Assigned to', accessor: 'AssignedTo', filter: multiConditionFilter },
      { Header: 'Created by', accessor: 'CreatedBy', filter: multiConditionFilter },
      {
        Header: 'Completed',
        accessor: 'CompletedDate',
        Cell: ({ row }: { row: { original: TaskData } }) => {
          const task = row.original;
          if (!task.CompletedDate) {
            return (
              <ProtectAction canAccess={rPermission?.canMarkAsComplete}>
                <div className='completed-cell'>
                  <div className='checkbox-container' onClick={() => onMarkAsCompleteTask(row.original)}>
                    <div className='custom-checkbox'></div>
                  </div>
                  <span>Mark as Complete</span>
                </div>
              </ProtectAction>
            );
          }
          return <span>{formatDate(task.CompletedDate, true)}</span>;
        },
        filter: multiConditionFilter,
      },
    ],
    [onEditTask, onDeleteTask],
  );

  const columnOrderMap = useMemo(() => {
    const map: Record<string, number> = {};

    if (Array.isArray(columnObjectsTask)) {
      columnObjectsTask.forEach((col: any) => {
        if (col?.key && typeof col.order === 'number') {
          map[col.key] = col.order;
        }
      });
    }
    return map;
  }, [columnObjectsTask]);
  const [columns, setColumns] = useState<Column<TaskData>[]>([]);

  useEffect(() => {
    setColumns(columnObjectsTask);
  }, []);

  const applyVisibleColumns = useCallback(
    (shouldSort = false) => {
      let filtered = allColumns.filter((col) => {
        const colKey = 'accessor' in col ? col.accessor : col.id;
        return visibleColumns.includes(colKey as string);
      });

      if (shouldSort) {
        filtered = filtered.sort((a, b) => {
          const aKey = 'accessor' in a ? a.accessor : a.id;
          const bKey = 'accessor' in b ? b.accessor : b.id;
          return visibleColumns.indexOf(aKey as string) - visibleColumns.indexOf(bKey as string);
        });
      }

      setColumns(filtered);
    },

    [allColumns, visibleColumns, columnOrderMap, columnObjectsTask],
  );

  const defaultColumn: any = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: (props: any) => (
        <DefaultColumnFilter
          {...props}
          clearFilterForColumn={clearFilterForColumn}
          currentFiltersRef={currentFiltersRef}
        />
      ),
    }),
    [],
  );
  const instance = useTable<TaskData>(
    {
      columns,
      data,
      defaultColumn: defaultColumn,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
    //setFilter,
    setAllFilters,
  } = instance as TableInstance<TaskData> & {
    setGlobalFilter: (filterValue: string | undefined) => void;
    //setFilter: (columnId: string, filterValue: any) => void;
    setAllFilters: (filters: any[]) => void;
  };

  const clearFilterForColumn = (columnId: string) => {
    const remainingFilters = currentFiltersRef.current.filter((filter) => filter.columnId !== columnId);
    // Remove the filter for the specified column ID
    setCurrentFilters(remainingFilters);

    if (remainingFilters.length === 0) {
      setAllFilters([]); // Clear all filters if no remaining filters
    } else {
      setAllFilters(
        remainingFilters.map((filter) => ({
          id: filter.columnId,
          value: filter.filterValue,
        })),
      );
    }
    // remainingFilters.forEach((filter) => {
    //   // Apply the remaining filters to the table
    //   setFilter(filter.columnId, filter.filterValue);
    // });
  };

  useEffect(() => {
    applyVisibleColumns(true);
  }, [applyTrigger, isResetClicked]);

  useEffect(() => {
    setGlobalFilter(searchTerm);
  }, [searchTerm, setGlobalFilter]);

  useEffect(() => {
    if (onFilteredSortedDataReady) {
      onFilteredSortedDataReady(rows.map((row) => row.original));
    }
  }, [rows, onFilteredSortedDataReady]);

  if (isLoading) {
    return (
      <div className='is-loading'>
        <Loader />
      </div>
    );
  }
  return (
    <section className='table-container-wrapper'>
      <SuccessfullToast
        isShowEnabled={saveConfigResponse}
        message={successMessage}
        callback={() => setSuccessMessage('')}
      />
      <ErrorToast isShowEnabled={!!errorMessage} message={errorMessage} callback={() => setErrorMessage('')} />
      <div className='table-container mb-[24px]'>
        <div className='task-table-scroll' style={{ minHeight: isRenderFromTaskTab ? 'calc(70vh - 200px)' : '' }}>
          <table {...getTableProps()} className='data-table task-detail-table'>
            <thead>
              {headerGroups.map((headerGroup: HeaderGroup<TaskData>) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((col) => {
                    const column = col as unknown as ColumnInstance<TaskData> & UseSortByColumnProps<TaskData> as any;
                    return (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className={`h-[44px] pt-[16px] pb-[16px] pl-[12px] pr-[12px] border-t border-[#4a5463] text-left ${column.id}-column`}
                      >
                        <div className='flex gap-[4px] items-center task-detail'>
                          <span className='text-[12px] font-semibold text-[#f7f9f9]'>{column.render('Header')}</span>
                          {column.isSorted && (
                            <span
                              className={`tdt-sort-icon ${
                                column.isSorted ? (column.isSortedDesc ? 'desc' : 'asc') : 'default'
                              }`}
                              style={{ display: column.render('Header') ? 'inline-block' : 'none' }}
                            />
                          )}

                          <span>{column.canFilter ? column.render('Filter') : null}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        className={`${cell.column.id === 'actions' ? 'actions-column' : ''}`}
                        style={{ textAlign: 'left' }}
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className='no-matching-records-container text-center p-4 pl-6 pr-6 h-[250px] flex items-center justify-center'>
              <span className='no-matching-records-message'>No matching records found</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TaskDataTable;
